import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  catalogConfigSelector,
  treeIdByCatalogIdSelector,
} from '../../../store/selectors/tree/data';
import { selectSourcePane, selectTargetPane } from '../../../store/slices/pane';
import { NODE_COPY } from '../../../utils/command';
import { getCatalogId } from '../../../utils/tree/ids';
import { getPathById, hasPasteOption } from '../../../utils/tree/treeNode';
import {
  NODE_TYPE_CATALOG,
  NODE_TYPE_CATEGORY,
  NODE_TYPE_ITEM,
  NODE_TYPE_MULTI_SELECT,
} from '../../../utils/types/nodeTypes';
import { getCatalogActions } from './helpers/getCatalogActions';
import { getCategoryActions } from './helpers/getCategoryActions';
import { getItemActions } from './helpers/getItemActions';
import { getMultiselectActions } from './helpers/getMultiselectActions';
import type { NodeBase, NodeItem } from '../../../entities/Node';
import type { AppThunkDispatch, RootState } from '../../../store';
import { selectCurrentCommand } from '../../../store/slices/treeDrag';

export const useHoverActionsNodeConfig = (node: NodeBase | NodeItem) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const nodeItem = useMemo(
    () => ({
      node,
      path: getPathById(node.id),
    }),
    [node],
  );
  const targetTree = useSelector((state: RootState) =>
    treeIdByCatalogIdSelector(state, node.catalogId),
  );
  const {
    directive,
    nodeItems,
    tree: sourceTree,
  } = useSelector(selectCurrentCommand);
  const catalogConfig = useSelector((state: RootState) =>
    catalogConfigSelector(state, node.catalogId),
  );
  const sourceCatalogId = getCatalogId(sourceTree);

  // Get the catalogs currently open in both panes
  const sourcePaneCatalog = useSelector(selectSourcePane);
  const targetPaneCatalog = useSelector(selectTargetPane);

  // Determine the other pane's catalog type for cross-pane copy validation
  const otherPane = useMemo(() => {
    if (!sourcePaneCatalog || !targetPaneCatalog) return undefined;
    return node.catalogId === sourcePaneCatalog.catalogId
      ? targetPaneCatalog
      : sourcePaneCatalog;
  }, [node.catalogId, sourcePaneCatalog, targetPaneCatalog]);

  const isCopy = useMemo(
    () => directive === NODE_COPY || node.catalogId !== sourceCatalogId,
    [directive, node.catalogId, sourceCatalogId],
  );

  const actions = useMemo(() => {
    if (!catalogConfig.allowHoverActions) {
      return {};
    }
    switch (node.type) {
      case NODE_TYPE_MULTI_SELECT:
        return getMultiselectActions(catalogConfig, {
          dispatch,
          nodeItem,
          tree: targetTree,
        });
      case NODE_TYPE_CATALOG:
        return getCatalogActions(catalogConfig, {
          directive,
          dispatch,
          isCopy,
          sourceNodeItems: nodeItems,
          targetNodeItem: nodeItem,
          tree: targetTree,
        });
      case NODE_TYPE_CATEGORY:
        return getCategoryActions(catalogConfig, {
          directive,
          dispatch,
          isCopy,
          nodeItem,
          nodeItems,
          otherPane,
          sourceTree,
          tree: targetTree,
        });
      case NODE_TYPE_ITEM:
        return getItemActions(catalogConfig, {
          dispatch,
          nodeItem,
          tree: targetTree,
        });

      default:
        return {};
    }
  }, [
    directive,
    dispatch,
    isCopy,
    node.type,
    nodeItem,
    nodeItems,
    otherPane,
    sourceTree,
    targetTree,
    catalogConfig,
  ]);

  const nodeConfig = useMemo(
    () => ({
      actions,
      onHoverOnly: node.type !== NODE_TYPE_MULTI_SELECT,
      showPaste: !!nodeItems?.length && hasPasteOption(node),
    }),
    [actions, node, nodeItems?.length],
  );

  return nodeConfig;
};
